<script setup lang="ts">
import { computed, getCurrentInstance, onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useCompanyStore } from '@Stores/company.store';
import { useLocationStore } from '@Stores/location.store';
import { useDeviceAuthStore } from '@Stores/deviceAuth.store';
import useCompanyImageBaseUrl from '@Use/useCompanyImageBaseUrl';
import useFlowNavigation from '@Use/useFlowNavigation';
import { verifyEmailCodeApi, resendEmailLoginCodeApi } from '@Api/otp.api';
import { useCountdownTimer } from '@Use/useCountdownTimer';
import { useOtpCodeVerification } from '@Use/useOtpCodeVerification';

import { GuidedShowcaseLayout, ActionBarLayout } from '@Shared';
import ShowcaseImage from '@Components/ShowcaseImage.vue';
import OtpLockedState from '@Components/OtpLockedState.vue';

const EMAIL_CODE_COOLDOWN_SECONDS = 30;

const { proxy } = getCurrentInstance();
const companyStore = useCompanyStore();
const locationStore = useLocationStore();
const companyImageBaseUrl = useCompanyImageBaseUrl();
const deviceAuthStore = useDeviceAuthStore();
const { loginHint } = storeToRefs(deviceAuthStore);
const { advanceFlow, goBackInFlow } = useFlowNavigation();
const { isCountdownRunning, timeLeft, start } = useCountdownTimer(EMAIL_CODE_COOLDOWN_SECONDS);

// Shared OTP verify surface (inline error, locked state, sending flag,
// multi-channel detection, try-again copy, channel switch, the shared post-verify
// routing, and the shared send/resend skeleton). Channel-specific bits are injected
// at call sites.
const {
    inlineError,
    codeLocked,
    isSendingCode,
    hasMultipleChannels,
    tryAnotherWay,
    handleVerifyResponse,
    performResend,
} = useOtpCodeVerification();

const emailCode = ref('');
const isVerifying = ref(false);

const hasEmailHint = computed(() => !!loginHint.value);

const verifyCode = async () => {
    if (isVerifying.value) return;
    isVerifying.value = true;
    inlineError.value = null;

    try {
        const response = await verifyEmailCodeApi({ code: emailCode.value });

        // Shared post-verify routing (statusCode-wins + sub-flow fallback).
        // Email "Passed" toasts then advances the flow.
        await handleVerifyResponse(response, {
            advanceFlow,
            clearCode: () => { emailCode.value = ''; },
            onPassed: async () => {
                proxy.$apptoast('An access code is being sent to your email.');
                await advanceFlow();
            },
        });
    } catch (err) {
        console.error('[Auth MFE] verifyEmailCodeApi error:', err);
        proxy.$apptoast('An error occurred verifying the code. Please try again.');
    } finally {
        isVerifying.value = false;
    }
};

const resendCode = (bypassCooldown = false) =>
    performResend({
        bypassCooldown,
        isCountdownRunning: () => isCountdownRunning.value,
        start,
        sendFn: resendEmailLoginCodeApi,
        successToast: 'A new code has been sent.',
        clearCode: () => { emailCode.value = ''; },
    });

// Locked-state Resend (CodeRetriesExhausted): the current code is dead, so bypass the resend
// cooldown — forcing a 30s wait here is broken UX (there is nothing left to rate-limit).
const resendFromLocked = () => resendCode(true);

onMounted(() => {
    start();
});
</script>

<template>
    <guided-showcase-layout
        suppress-back-to-dashboard
        title="Check your email"
        :company="companyStore.company"
        :brand="companyStore.brand"
        :location="locationStore.location"
        :company-image-base-url="companyImageBaseUrl"
        :steps="['verify-email', 'verify-code']"
        current-step="0">

        <app-form-content-layout>
            <!-- Inline locked state: current code's retries exhausted, session
                 still valid. Shown in place of the input; resend or switch
                 channel to continue. -->
            <otp-locked-state
                v-if="codeLocked"
                :has-multiple-channels="hasMultipleChannels"
                :is-sending="isSendingCode"
                @resend="resendFromLocked"
                @try-another-way="tryAnotherWay" />

            <template v-else>
                <p class="verify-email-code-view__delivery-copy">
                    We've sent a security code to: <br />
                    <strong v-if="hasEmailHint" data-testid="redacted-identifier">{{ loginHint }}</strong>
                </p>

                <app-passcode-input
                    v-model="emailCode"
                    class="verify-email-code-view__passcode"
                    auto-focus />

                <p v-if="inlineError" class="verify-email-code-view__error" role="alert" data-testid="inline-error">
                    {{ inlineError }}
                </p>

                <div class="verify-email-code-view__subcontent">
                    <span v-if="isCountdownRunning">
                        You can request a new code in {{ timeLeft }} seconds.
                    </span>
                    <template v-else>
                        <app-link
                            v-if="!hasMultipleChannels"
                            class="verify-email-code-view__resend-code"
                            type="internal"
                            data-testid="resend-link"
                            @onClick="resendCode">
                            {{ isSendingCode ? 'Sending...' : 'Resend' }}
                        </app-link>
                        <div v-else class="verify-email-code-view__footer-links">
                            <app-link
                                type="internal"
                                data-testid="resend-link"
                                @onClick="resendCode">
                                {{ isSendingCode ? 'Sending...' : 'Resend code' }}
                            </app-link>
                            <app-link
                                type="internal"
                                data-testid="try-another-way-link"
                                @onClick="tryAnotherWay">
                                Try another way
                            </app-link>
                        </div>
                    </template>
                </div>
            </template>

        </app-form-content-layout>

        <action-bar-layout v-if="!codeLocked" @back-click="goBackInFlow">
            <app-button
                size="large"
                :is-loading="isVerifying"
                expanded
                data-id="continue"
                @onClick="verifyCode">
                    Continue
            </app-button>
        </action-bar-layout>

        <template #branded>
            <showcase-image />
        </template>
        
    </guided-showcase-layout>
</template>

<style lang="scss" scoped>
@use 'certifid-v2-client-hub-styles' as *;

.verify-email-code-view {
    // Center the passcode row within the form column and cap its width so
    // cells stay legible on desktop.
    &__passcode {
        max-width: 22rem;
        margin-inline: auto;

        :deep(.app-passcode-input) {
            width: 100%;
            justify-content: center;
        }

        :deep(.app-passcode-input .digit-box) {
            flex: 1;
            max-width: 3.25rem;
        }
    }

    &__delivery-copy {
        line-height: 1.5;
    }

    &__error {
        color: $red;
        margin: 0.5rem 0;
    }

    &__subcontent {
        font-size: 0.9rem;
    }

    &__footer-links {
        display: flex;
        gap: 1.5rem;
        flex-wrap: wrap;
    }

}
</style>
